'use client'

import { memo } from 'react'
import { clsx } from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { Image } from '../ui/image'
import { DownloadIcon, Info } from 'lucide-react'
import { IcInfo } from '~/icons/ic_info'

import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
interface QRCodeDisplayProps {
  qrCodeBase64: string
  onDownload: () => void
  downloadButtonText: string
}

const QRCodeDisplay = ({ qrCodeBase64, onDownload, downloadButtonText }: QRCodeDisplayProps) => {
  const { locale } = useLocale()
  const t = getTranslation(locale)
  return (
    <div className="px-3 py-4 pb-16 pt-2 sm:px-4 sm:py-6 sm:pb-24 sm:pt-4">
      <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2 md:items-center md:gap-8">
        {/* QR Section */}
        <div className="flex flex-col items-center">
          <Image
            className="mb-2"
            src="/static/images/bidv_logo.png"
            alt="BIDV Logo"
            width={80}
            height={26}
          />

          <div className="relative grid justify-center">
            <Image
              className="h-[140px] w-[140px] border border-solid border-[#016B69] sm:h-[160px] sm:w-[160px]"
              src={qrCodeBase64}
              alt="QR Code"
              width={160}
              height={160}
              objectFit="contain"
            />
            <Image
              className="absolute inset-1/2 z-10 h-[30px] w-[30px] -translate-x-1/2 -translate-y-24 opacity-95 sm:h-[35px] sm:w-[35px] sm:-translate-y-28"
              src="/static/images/yellow_apricot.png"
              alt="Yellow Apricot"
              width={35}
              height={35}
            />
          </div>

          <div className="relative -top-4 flex justify-center gap-2 sm:-top-5">
            <Image src="/static/images/napas.png" alt="Napas" height={20} width={98} />
            <div className="h w-[1px] bg-[#E5E6E8]" />
            <Image src="/static/images/vietqr.png" alt="VietQR" height={50} width={62} />
          </div>

          <button
            onClick={onDownload}
            className="mt-4 flex items-center justify-center gap-2 rounded bg-primary-500 px-4 py-2 text-sm text-white"
          >
            <DownloadIcon width={14} height={14} />
            {downloadButtonText}
          </button>
        </div>

        {/* Instruction Section */}
        <div className="mt-4 md:mt-0">
          <div className="mb-4 flex items-start gap-2 rounded-md bg-[#E7F7EA] px-[12px] py-[6px] text-sm text-[#2D2E32]">
            <IcInfo className="mt-[2px] h-4 w-4 text-[#047819]" />
            <p className="text-sm font-[350] text-[#2D2E32]">{t('payment.qrNotice')}</p>
          </div>
          <div>
            <h3 className="mb-4 text-lg font-semibold">{t('payment.qrGuideTitle')}</h3>
            <ol className="relative space-y-4 text-sm text-[#2D2E32] before:absolute before:left-[11px] before:top-4 before:h-[calc(100%-2rem)] before:w-px before:bg-gray-300">
              <li className="relative flex items-start gap-3">
                <span className="relative z-10 mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#F3F3F3] text-base font-medium">
                  1
                </span>
                <span className="mt-1 font-[300]">{t('payment.qrStep1')}</span>
              </li>
              <li className="relative flex items-start gap-3">
                <span className="relative z-10 mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#F3F3F3] text-base font-medium">
                  2
                </span>
                <span className="mt-1 font-[300]">{t('payment.qrStep2')}</span>
              </li>
              <li className="relative flex items-start gap-3">
                <span className="relative z-10 mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#F3F3F3] text-base font-medium">
                  3
                </span>
                <span className="mt-1 font-[300]">{t('payment.qrStep3')}</span>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  )
}

function arePropsEqual(prevProps: QRCodeDisplayProps, nextProps: QRCodeDisplayProps) {
  return (
    prevProps.qrCodeBase64 === nextProps.qrCodeBase64 &&
    prevProps.downloadButtonText === nextProps.downloadButtonText
  )
}

export default memo(QRCodeDisplay, arePropsEqual)
